import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { IMG } from '../lib/images';
import { SEO } from '../components/SEO';

export default function HeritagePage() {
  const { t } = useI18n();
  const sections = t('heritage.sections');
  const values = t('heritage.values');

  return (
    <main className="bg-background">
      <SEO
        title={t('heritage.title')}
        description="L'histoire de Tahitian Move depuis 1977 — une marque polynésienne fondée par Enio et Marina Chiocchi à Papeete."
      />
      {/* Hero header */}
      <section className="relative pt-40 pb-24 md:pt-48 md:pb-32 bg-paper overflow-hidden">
        <div className="absolute right-0 top-20 w-1/2 h-full opacity-10 hidden md:block">
          <img src={IMG.tiare} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="eyebrow reveal">1977 — 2025</p>
          <h1 className="display text-6xl md:text-8xl lg:text-9xl mt-6 max-w-4xl reveal reveal-delay-1">
            {t('heritage.subtitle')}
          </h1>
          <p className="mt-10 text-ink-soft max-w-xl text-base md:text-lg leading-relaxed reveal reveal-delay-2">
            {t('home.heritageBody')}
          </p>
        </div>
      </section>

      {/* Founder portrait */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-5 media-card aspect-[3/4]">
            <img src={IMG.heritageVintage} alt="Enio et Marina Chiocchi — Tahitian Move heritage" className="w-full h-full object-cover" />
          </div>
          <div className="md:col-span-7">
            <p className="eyebrow">Enio & Marina Chiocchi</p>
            <h2 className="display text-4xl md:text-5xl mt-4 text-ink">
              « Habiller la Polynésie d’élégance. »
            </h2>
            <p className="font-serif italic text-xl md:text-2xl text-ink-soft mt-8 leading-relaxed">
              En 1977, dans une boutique de Papeete, Enio et Marina Chiocchi posent les fondations d’une marque qui célébrera, des décennies durant, la beauté insulaire de la Polynésie française.
            </p>
            <p className="text-ink-soft mt-6 text-base md:text-lg leading-relaxed">
              Près de cinquante ans plus tard, Tahitian Move reste fidèle à cette vision : une mode pensée pour le rythme du Pacifique, libre des tendances, profondément ancrée dans son territoire.
            </p>
            <p className="text-ink-soft mt-6 text-base md:text-lg leading-relaxed">
              Originalité de ce label, c’est la création de modèles originaux classifiés dans des univers bien distincts, des univers aux noms évocateurs :
            </p>
            <p className="font-serif italic text-xl md:text-2xl text-ink mt-6 leading-relaxed">
              Tahiti d’antan, Hippy Chic, Polynesian Underground ou Jewels…
            </p>
            <p className="text-ink-soft mt-6 text-base md:text-lg leading-relaxed">
              Bref, des univers qui donnent déjà un avant‑goût de découverte et qui éveillent la curiosité.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-paper py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="eyebrow text-center">Chronologie</p>
          <h2 className="display text-5xl md:text-6xl mt-4 text-center text-ink">
            Une marque, une trajectoire.
          </h2>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-x-20 md:gap-y-24 relative">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-sand" />
            {sections.map((s, i) => (
              <div
                key={i}
                className={`relative ${i % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12 md:col-start-2'}`}
              >
                <div
                  className={`absolute top-2 w-3 h-3 rounded-full bg-lagoon hidden md:block ${
                    i % 2 === 0 ? '-right-[7px]' : '-left-[7px]'
                  }`}
                />
                <p className="font-serif text-5xl md:text-6xl text-lagoon">{s.year}</p>
                <h3 className="font-serif text-2xl md:text-3xl text-ink mt-4">{s.title}</h3>
                <p className="text-ink-soft mt-4 text-base font-light leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="eyebrow">{t('heritage.valuesTitle')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16 mt-12">
            {values.map((v, i) => (
              <div key={i} className="border-t border-sand pt-8">
                <p className="font-serif text-5xl text-lagoon">{String(i + 1).padStart(2, '0')}</p>
                <h3 className="font-serif text-3xl text-ink mt-6">{v.title}</h3>
                <p className="text-ink-soft mt-4 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Spirit — surf culture & Polynesian art-de-vivre */}
      <section className="bg-ocean text-white py-24 md:py-32 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-5 order-2 md:order-1">
            <p className="eyebrow text-lagoon">L’esprit Tahitian Move</p>
            <h2 className="display text-4xl md:text-5xl lg:text-6xl mt-6">
              Ancrée dans la culture surf, portée par le Pacifique.
            </h2>
            <p className="text-white/70 mt-8 text-base md:text-lg leading-relaxed">
              De Teahupo’o aux lagons de Moorea, Tahitian Move s’inspire d’une Polynésie en mouvement —
              celle des riders, des danseurs, des artisans. Une mode qui vit dehors, qui respire l’océan,
              et qui célèbre depuis 1977 l’art de vivre insulaire.
            </p>
          </div>
          <div className="md:col-span-7 order-1 md:order-2 media-card">
            <img src={IMG.heritageSurf} alt="Surf culture & Tahitian Move heritage" className="w-full h-auto" />
          </div>
        </div>
      </section>

      <section className="bg-paper py-24 md:py-32 border-t border-sand">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <h3 className="display text-4xl md:text-6xl max-w-2xl text-ink">
            Découvrez l’univers Tahitian Move.
          </h3>
          <Link to="/collections" className="btn-primary self-start md:self-auto">
            {t('common.discoverCollections')} <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
